import ClientForm, {
  ClientFormHandle,
  ClientFormValues,
} from 'components/featured/Client/ClientForm/ClientForm';
import ClientFormPage from 'components/featured/Client/ClientFormPage';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { CreateClientV2Payload } from 'model/client.model';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { HuddleClientService } from 'service/huddle-client.service';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/slices/toastSlice';

const ClientEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const clientFormRef = useRef<ClientFormHandle>(null);
  const [clientData, setClientData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [formValues, setFormValues] = useState<ClientFormValues | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { alertState, completeOperation, setAlertState, getAlertContent } = useAlertStatus();

  useEffect(() => {
    if (!id) return;
    const fetchClient = async () => {
      try {
        setLoading(true);
        const data = await HuddleClientService.getClientHuddleDetails(id);
        setClientData(data);
      } catch (err) {
        navigate(RoutePaths.Client, { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [id, navigate]);

  if (loading) return <>Loading client details…</>;
  if (!clientData) return null;

  const initialValues: Partial<ClientFormValues> = {
    clientType: clientData?.clientType ?? clientData?.category ?? '',
    salutation: clientData?.salutation ?? '',
    name: clientData?.Name ?? clientData?.name ?? '',
    linkEntity: Array.isArray(clientData?.parentClientId)
      ? clientData.parentClientId
      : clientData?.parentClientId
        ? [clientData.parentClientId]
        : Array.isArray(clientData?.linkEntity)
          ? clientData.linkEntity
          : clientData?.linkEntity
            ? [clientData.linkEntity]
            : [],
    agency: clientData?.agency ?? clientData?.agencyName ?? '',
    owner: clientData?.ownerId ?? clientData?.owner ?? clientData?.ownerName ?? '',
    league:
      clientData?.league ??
      (Array.isArray(clientData?.classification)
        ? clientData.classification[0]
        : clientData?.classification) ??
      '',
    team: clientData?.team ?? '',
    position: clientData?.position ?? clientData?.industry ?? '',
    number: clientData?.number ?? '',
    status: clientData?.status ?? '',
    college: clientData?.college ?? '',
    loanOutCompany: clientData?.loanOutCompany ?? clientData?.enterpriseName ?? '',
    mobile: clientData?.personMobilePhone ?? clientData?.mobile ?? clientData?.phone ?? '',
    phone: clientData?.phone ?? clientData?.Phone ?? '',
    email: clientData?.email ?? clientData?.Email ?? clientData?.personEmail ?? '',
    personalEmail: clientData?.personEmail ?? '',
    profileImageUrl: clientData?.profileImageUrl ?? clientData?.avatar ?? '',
    billingStreet: clientData?.billingAddress?.street ?? clientData?.billingStreet ?? '',
    billingCity: clientData?.billingAddress?.city ?? clientData?.billingCity ?? '',
    billingState: clientData?.billingAddress?.state ?? clientData?.billingState ?? '',
    billingZip: clientData?.billingAddress?.postalCode ?? clientData?.billingZip ?? '',
    billingCountry: clientData?.billingAddress?.country ?? clientData?.billingCountry ?? '',
    mailingStreet: clientData?.personMailingAddress?.street ?? clientData?.mailingStreet ?? '',
    mailingCity: clientData?.personMailingAddress?.city ?? clientData?.mailingCity ?? '',
    mailingState: clientData?.personMailingAddress?.state ?? clientData?.mailingState ?? '',
    mailingZip: clientData?.personMailingAddress?.postalCode ?? clientData?.mailingZip ?? '',
    mailingCountry: clientData?.personMailingAddress?.country ?? clientData?.mailingCountry ?? '',
    bio: clientData?.bio ?? '',
  };

  const handleChange = (values: ClientFormValues, valid: boolean) => {
    setFormValues(values);
    setIsValid(valid);
  };

  const handleSubmit = async () => {
    if (!formValues || !isValid || !id) return;
    setSubmitting(true);
    try {
      const profileImageUrl = (await clientFormRef.current?.uploadImage()) ?? null;
      const organizationId = sessionStorage.getItem('organizationId') || undefined;
      const PRODUCTION_TYPE = 'production co';
      const type = formValues.clientType?.toLocaleLowerCase() || '';
      const payload: CreateClientV2Payload = {
        name: formValues.name,
        organizationId,
        profileImageUrl: profileImageUrl || undefined,
        clientType: type === PRODUCTION_TYPE ? PRODUCTION_TYPE.replace(' ', '_') : type,
        salutation: formValues.salutation || undefined,
        league: formValues.league || undefined,
        classification: formValues.league ? [formValues.league] : undefined,
        position: formValues.position || undefined,
        agency: formValues.agency || undefined,
        email: formValues.email || undefined,
        personEmail: formValues.personalEmail || undefined,
        personMobilePhone: formValues.mobile || undefined,
        phone: formValues.phone || undefined,
        team: formValues.team || undefined,
        college: formValues.college || undefined,
        status: formValues.status || undefined,
        number: formValues.number || undefined,
        bio: formValues.bio || undefined,
        ...((formValues.billingStreet ||
          formValues.billingCity ||
          formValues.billingState ||
          formValues.billingCountry ||
          formValues.billingZip) && {
          billingAddress: {
            street: formValues.billingStreet || undefined,
            city: formValues.billingCity || undefined,
            state: formValues.billingState || undefined,
            country: formValues.billingCountry || undefined,
            postalCode: formValues.billingZip || undefined,
          },
        }),
        ...((formValues.mailingStreet ||
          formValues.mailingCity ||
          formValues.mailingState ||
          formValues.mailingCountry ||
          formValues.mailingZip) && {
          personMailingAddress: {
            street: formValues.mailingStreet || undefined,
            city: formValues.mailingCity || undefined,
            state: formValues.mailingState || undefined,
            country: formValues.mailingCountry || undefined,
            postalCode: formValues.mailingZip || undefined,
          },
        }),
        ownerName: formValues.owner || undefined,
        parentClientId: formValues.linkEntity?.[0] || undefined,
        enterpriseName: formValues.loanOutCompany || undefined,
        loanOutCompany: formValues.loanOutCompany || undefined,
        captiv8Id: clientData?.captiv8Id ?? clientData?.captive8Id ?? undefined,
        slackChannelId: clientData?.slackChannelId ?? undefined,
      };
      await HuddleClientService.editHuddleClient(payload, id);
      dispatch(showToast({ message: 'Client updated successfully' }));
      navigate(-1);
    } catch (err: any) {
      completeOperation('update', err?.message ?? 'Failed to update client', true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ClientFormPage
        isEdit={true}
        onSubmit={handleSubmit}
        submitting={submitting}
        primaryDisabled={!isValid}
      >
        <ClientForm ref={clientFormRef} initialValues={initialValues} onChange={handleChange} />
      </ClientFormPage>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </>
  );
};

export default ClientEdit;
